import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { Field, FieldGrid, ToggleField } from "@/components/shared/form";
import { StatusDot } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  useDeleteUom,
  useUoms,
  useUpsertUom,
} from "@/features/materials/api";
import { emptyUomForm, uomFormSchema, type UomFormValues } from "@/features/materials/schema";
import type { Uom } from "@/features/materials/types";

export function UomsPanel() {
  const { data = [], isLoading, error } = useUoms();
  const upsert = useUpsertUom();
  const remove = useDeleteUom();
  const [editing, setEditing] = useState<Uom | null>(null);
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState<Uom | null>(null);

  const form = useForm<UomFormValues>({
    resolver: zodResolver(uomFormSchema),
    defaultValues: emptyUomForm,
  });

  function openCreate() {
    setEditing(null);
    form.reset(emptyUomForm);
    setOpen(true);
  }

  function openEdit(row: Uom) {
    setEditing(row);
    form.reset({ code: row.code, name: row.name, is_active: row.is_active });
    setOpen(true);
  }

  const submit = form.handleSubmit(async (values) => {
    await upsert.mutateAsync({ id: editing?.id, values });
    setOpen(false);
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">Units of measure for materials and stock</p>
        <Button type="button" size="sm" onClick={openCreate}>
          <Plus />
          Add UOM
        </Button>
      </div>

      {error ? (
        <p className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
          {(error as Error).message}
        </p>
      ) : null}

      <div className="overflow-hidden rounded-lg border border-border bg-card">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-2xs uppercase tracking-[0.05em] text-muted-foreground">
              <th className="px-3 py-2 font-semibold">Code</th>
              <th className="px-3 py-2 font-semibold">Name</th>
              <th className="px-3 py-2 font-semibold">Status</th>
              <th className="px-3 py-2 font-semibold" />
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={4} className="px-3 py-8 text-center text-xs text-muted-foreground">
                  Loading…
                </td>
              </tr>
            ) : (
              data.map((row) => (
                <tr key={row.id} className="border-b border-border/70">
                  <td className="px-3 py-1.5 font-mono text-xs font-medium">{row.code}</td>
                  <td className="px-3 py-1.5">{row.name}</td>
                  <td className="px-3 py-1.5">
                    <StatusDot tone={row.is_active ? "success" : "neutral"}>
                      {row.is_active ? "Active" : "Inactive"}
                    </StatusDot>
                  </td>
                  <td className="px-3 py-1.5 text-right">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      aria-label={`Edit ${row.code}`}
                      onClick={() => openEdit(row)}
                    >
                      <Pencil />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      aria-label={`Delete ${row.code}`}
                      className="hover:text-destructive"
                      onClick={() => setDeleting(row)}
                    >
                      <Trash2 />
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Edit UOM" : "New UOM"}</DialogTitle>
          </DialogHeader>
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
          >
            <FieldGrid>
              <Field
                label="Code"
                required
                span={4}
                error={form.formState.errors.code?.message}
                htmlFor="uom-code"
              >
                <Input id="uom-code" className="uppercase" {...form.register("code")} />
              </Field>
              <Field
                label="Name"
                required
                span={4}
                error={form.formState.errors.name?.message}
                htmlFor="uom-name"
              >
                <Input id="uom-name" {...form.register("name")} />
              </Field>
              <ToggleField
                label="Active"
                checked={form.watch("is_active")}
                onChange={(v) => form.setValue("is_active", v)}
                span={4}
              />
            </FieldGrid>
            <DialogFooter>
              <Button type="button" variant="outline" size="sm" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" size="sm" disabled={upsert.isPending}>
                Save
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={(o) => {
          if (!o) setDeleting(null);
        }}
        title="Delete UOM?"
        description={
          deleting
            ? `${deleting.code} cannot be deleted if materials still reference it.`
            : undefined
        }
        confirmLabel="Delete"
        destructive
        loading={remove.isPending}
        onConfirm={async () => {
          if (!deleting) return;
          await remove.mutateAsync(deleting.id);
          setDeleting(null);
        }}
      />
    </div>
  );
}
